// The six tones of an engineering outcome (Master Prompt A25 (a), (d)) and where each
// hook of the shared contracts gets its tone from. One vocabulary for the status banner,
// the check-result pills and the warning cards, so an icon and a colour always mean the
// same thing across the ten modules.

export type StatusTone = "pass" | "warning" | "fail" | "review" | "not-evaluated" | "partial";

/**
 * The tone of a result status: the summary's own map gives pass / warning / fail, and
 * REVIEW_REQUIRED is drawn as the review tone (A25 (c)), not as a warning.
 */
export function bannerTone<S extends string>(
  status: S,
  tones: Record<S, "pass" | "fail" | "warning">,
): StatusTone {
  return status === "REVIEW_REQUIRED" ? "review" : tones[status];
}

/** The tone of a check-result cell (td[data-check-result]) by its outcome key. */
export function checkTone(key: string): StatusTone {
  switch (key) {
    case "pass":
      return "pass";
    case "fail":
      return "fail";
    case "review":
      return "review";
    case "partial":
      return "partial";
    default:
      // "not-evaluated" and "not-established": a check that was not made.
      return "not-evaluated";
  }
}

/**
 * The tone of every warning group any panel renders (styles/warningGroups.test.ts holds
 * study.css to this list): a failed check, evidence the engineer still owes, a check that
 * was not made, and the plain warnings including the declared-evidence limit.
 */
export const GROUP_TONE: Record<string, StatusTone> = {
  "not-met": "fail",
  "selectivity-not-met": "fail",
  "no-solution": "fail",
  "review-evidence": "review",
  "data-not-verified": "review",
  "fault-level": "review",
  "not-established": "review",
  "not-evaluated": "not-evaluated",
  other: "warning",
  "declared-evidence": "warning",
};
